/* =========================================================
   ØNWRD Bundle Builder — bundle-builder.js
   Self-contained ES5-compatible IIFE. Multiple instances
   on one page are fully independent.
   ========================================================= */

(function () {
  'use strict';

  /* ── Utilities ─────────────────────────────────────────── */

  function uid() {
    return Math.random().toString(36).slice(2, 9) + Math.random().toString(36).slice(2, 9);
  }

  function formatPrice(cents) {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 2,
    }).format(cents / 100);
  }

  function escHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function truncate(str, max) {
    str = String(str || '');
    return str.length > max ? str.slice(0, max - 1) + '\u2026' : str;
  }

  var CALIBRATE = typeof URLSearchParams !== 'undefined' &&
    new URLSearchParams(window.location.search).get('bb-calibrate') === '1';

  /* ── BundleBuilder class ───────────────────────────────── */

  function BundleBuilder(rootEl, data) {
    this.root      = rootEl;
    this.sectionId = data.sectionId;
    this.settings  = data.settings;
    this.organizers = data.organizers;
    this.modules    = data.modules;

    /* Runtime state — all mutable state lives here */
    this._state = {
      step: 1,
      organizer: null,       // selected organizer obj with variantId/price/image merged in
      placements: [],        // [{uid, productId, variantId, variantTitle, x, y, w, h, rotated, rotatable, image, title, price}]
      pendingModule: null,   // {module, variantId} — tap-to-place mode
      dragging: null,        // active pointer drag state
    };

    /* Per-module selected variant cache: productId → variantId */
    this._moduleVariants = {};
    this.modules.forEach(function (m) {
      this._moduleVariants[m.productId] = m.variants[0].id;
    }, this);

    this._previewCell = null; // keyboard-driven preview position {x,y}

    this._buildSkeleton();
    this._bindEvents();
    this._renderAll();
  }

  /* ── Skeleton ────────────────────────────────────────────
     Build the fixed HTML shell once. Dynamic content goes
     inside named slots rendered by _render* methods.
  ──────────────────────────────────────────────────────── */

  BundleBuilder.prototype._buildSkeleton = function () {
    this.root.innerHTML = [
      '<div class="bb-inner">',
        '<nav class="bb-progress" aria-label="Builder steps">',
          '<div class="bb-progress__steps" id="bb-progress-' + this.sectionId + '"></div>',
        '</nav>',

        '<section class="bb-step" data-step="1" aria-labelledby="bb-step1-h-' + this.sectionId + '">',
        '</section>',

        '<section class="bb-step" data-step="2" hidden>',
          '<div class="bb-builder">',
            '<div class="bb-builder__grid-area"></div>',
            '<div class="bb-builder__tray"></div>',
          '</div>',
        '</section>',

        '<section class="bb-step" data-step="3" hidden>',
          '<div class="bb-summary"></div>',
        '</section>',

        '<div class="bb-error" hidden role="alert" aria-live="assertive"></div>',

        '<div class="bb-drag-ghost" aria-hidden="true" style="display:none;position:fixed;pointer-events:none;z-index:9999;"></div>',
      '</div>',
    ].join('');

    this._els = {
      progressSteps: this.root.querySelector('.bb-progress__steps'),
      step1: this.root.querySelector('[data-step="1"]'),
      step2: this.root.querySelector('[data-step="2"]'),
      step3: this.root.querySelector('[data-step="3"]'),
      gridArea:    this.root.querySelector('.bb-builder__grid-area'),
      tray:        this.root.querySelector('.bb-builder__tray'),
      summary:     this.root.querySelector('.bb-summary'),
      error:       this.root.querySelector('.bb-error'),
      ghost:       this.root.querySelector('.bb-drag-ghost'),
      // Set by _initGridPreview after each _renderGridArea:
      gridOverlay: null,
      preview:     null,
    };
  };

  /* ── Event delegation ────────────────────────────────────
     All events are delegated to the root so re-rendering
     innerHTML never breaks listener attachment.
  ──────────────────────────────────────────────────────── */

  BundleBuilder.prototype._bindEvents = function () {
    var self = this;
    this.root.addEventListener('click',       function (e) { self._onClick(e); });
    this.root.addEventListener('change',      function (e) { self._onChange(e); });
    this.root.addEventListener('keydown',     function (e) { self._onKeydown(e); });
    this.root.addEventListener('pointerdown', function (e) { self._onPointerDown(e); });
    // NOTE: No pointermove listener here. Hover preview is handled by per-cell
    // mouseover delegation wired in _initGridPreview after each _renderGridArea.
  };

  /* ── Top-level render ─────────────────────────────────── */

  BundleBuilder.prototype._renderAll = function () {
    var s = this._state;
    this._renderProgress();

    // Show / hide steps
    this._els.step1.hidden = (s.step !== 1);
    this._els.step2.hidden = (s.step !== 2);
    this._els.step3.hidden = (s.step !== 3);

    if (s.step === 1) this._renderOrganizerPicker();
    if (s.step === 2 && s.organizer) { this._renderGridArea(); this._renderTray(); }
    if (s.step === 3 && s.organizer) this._renderSummary();
  };

  /* ── Progress bar ─────────────────────────────────────── */

  BundleBuilder.prototype._renderProgress = function () {
    var s = this._state;
    var labels = ['Choose Panel', 'Build Layout', 'Review'];
    var html = '';
    for (var n = 1; n <= 3; n++) {
      var disabled = (n === 2 && !s.organizer) || (n === 3 && s.placements.length === 0 && s.step < 3);
      html += [
        '<button class="bb-progress__step',
          n === s.step ? ' is-active' : '',
          n < s.step  ? ' is-done'   : '',
        '"',
        ' data-action="goto-step" data-step="' + n + '"',
        ' aria-current="' + (n === s.step ? 'step' : 'false') + '"',
        disabled ? ' disabled' : '',
        '>',
          '<span class="bb-progress__num">' + n + '</span>',
          '<span class="bb-progress__label">' + labels[n - 1] + '</span>',
        '</button>',
        n < 3 ? '<div class="bb-progress__line" aria-hidden="true"></div>' : '',
      ].join('');
    }
    this._els.progressSteps.innerHTML = html;
  };

  /* ── Step 1: Organizer picker ─────────────────────────── */

  BundleBuilder.prototype._renderOrganizerPicker = function () {
    var s = this._state;
    var self = this;
    var cards = this.organizers.map(function (org) {
      return self._orgCardHtml(org, s.organizer);
    }).join('');

    this._els.step1.innerHTML = [
      '<div class="bb-organizer-picker">',
        CALIBRATE ? '<div class="bb-cal-banner">⬛ Calibration mode — select a panel to open the calibration tool.</div>' : '',
        '<h2 class="bb-section-heading" id="bb-step1-h-' + this.sectionId + '">',
          escHtml(this.settings.heading || 'Build Your Bundle'),
        '</h2>',
        '<p class="bb-section-desc">Choose your ØNWRD panel, then pick a colour.</p>',
        '<div class="bb-organizer-grid" role="listbox" aria-label="Organizer panels">',
          cards,
        '</div>',
      '</div>',
    ].join('');
  };

  BundleBuilder.prototype._orgCardHtml = function (org, selectedOrg) {
    var isSel = selectedOrg && selectedOrg.productId === org.productId;
    var defaultVariant = org.variants.filter(function (v) { return v.available; })[0] || org.variants[0];
    var selVariantId = isSel ? selectedOrg.variantId : defaultVariant.id;
    var selVariant = org.variants.filter(function (v) { return v.id === selVariantId; })[0] || org.variants[0];

    var variantSwatches = org.variants.map(function (v) {
      var soldOut = !v.available;
      return [
        '<label class="bb-variant-swatch' + (soldOut ? ' bb-variant-swatch--soldout' : '') + '">',
          '<input type="radio" name="org-variant-' + org.productId + '"',
            ' value="' + v.id + '"',
            ' data-action="select-org-variant"',
            ' data-product-id="' + org.productId + '"',
            v.id === selVariantId ? ' checked' : '',
            soldOut ? ' disabled' : '',
          '>',
          '<span class="bb-variant-swatch__label">' + escHtml(v.title) +
            (soldOut ? '<span class="bb-variant-swatch__soldout"> — Sold out</span>' : '') +
          '</span>',
        '</label>',
      ].join('');
    }).join('');

    var previewImg = selVariant.image || org.previewImage;

    return [
      '<div class="bb-org-card' + (isSel ? ' is-selected' : '') + '"',
        ' role="option" aria-selected="' + (isSel ? 'true' : 'false') + '"',
        ' data-action="select-organizer" data-product-id="' + org.productId + '"',
        ' tabindex="0"',
      '>',
        '<div class="bb-org-card__img-wrap">',
          '<img class="bb-org-card__img"',
            ' src="' + escHtml(previewImg) + '"',
            ' alt="' + escHtml(org.title) + '"',
            ' loading="lazy"',
          '>',
        '</div>',
        '<div class="bb-org-card__body">',
          '<h3 class="bb-org-card__title">' + escHtml(org.title) + '</h3>',
          '<p class="bb-org-card__price">' + formatPrice(selVariant.price) + '</p>',
          org.variants.length > 1 ? '<div class="bb-org-card__variants" role="radiogroup" aria-label="Choose colour">' + variantSwatches + '</div>' : '',
          '<button class="bb-btn bb-btn--primary bb-org-card__cta"',
            ' data-action="confirm-organizer" data-product-id="' + org.productId + '"',
            isSel ? '' : ' disabled',
          '>',
            isSel ? 'Continue with this Panel →' : 'Select This Panel',
          '</button>',
        '</div>',
      '</div>',
    ].join('');
  };

  /* ── Step 2: Grid area ────────────────────────────────── */

  BundleBuilder.prototype._renderGridArea = function () {
    var s = this._state;
    var org = s.organizer;

    var pm = s.pendingModule;
    var footerMsg = pm
      ? 'Placing <strong>' + escHtml(pm.module.title) + '</strong>' +
        (pm.module.rotatable ? ' &mdash; press <kbd>R</kbd> to rotate' : '') +
        '. <button class="bb-link" data-action="cancel-pending" aria-label="Cancel placement">Cancel (Esc)</button>'
      : s.placements.length === 0
        ? 'Drag a component onto the grid, or use the <strong>+ Place</strong> buttons.'
        : s.placements.length + ' component' + (s.placements.length !== 1 ? 's' : '') + ' placed.';

    this._els.gridArea.innerHTML = [
      '<div class="bb-grid-panel">',
        '<div class="bb-grid-header">',
          '<span class="bb-grid-panel__name">' + escHtml(org.title) +
            (org.variantTitle && org.variantTitle !== 'Default Title'
              ? ' — ' + escHtml(org.variantTitle) : '') +
          '</span>',
          '<button class="bb-btn bb-btn--ghost bb-grid-header__change"',
            ' data-action="goto-step" data-step="1"',
            ' aria-label="Change organizer panel">Change Panel</button>',
        '</div>',
        '<div class="bb-grid-viewport"',
          ' style="--bb-cols:' + org.cols + ';--bb-rows:' + org.rows + ';--bb-cs:' + this.settings.cellSize + 'px"',
        '>',
          '<div class="bb-grid-preview-wrap"',
            ' aria-label="Placement grid, ' + org.cols + ' columns by ' + org.rows + ' rows"',
          '>',
            '<img class="bb-grid-preview"',
              ' src="' + escHtml(org.previewImage) + '"',
              ' alt="' + escHtml(org.title) + ' organizer"',
              ' loading="lazy"',
            '>',
            '<div class="bb-grid-overlay' + (s.pendingModule ? ' bb-grid-overlay--placing' : '') + '"',
              ' style="--bb-cols:' + org.cols + ';--bb-rows:' + org.rows + ';',
                'left:' + (org.bounds.x * 100) + '%;top:' + (org.bounds.y * 100) + '%;',
                'width:' + (org.bounds.w * 100) + '%;height:' + (org.bounds.h * 100) + '%"',
              ' role="grid"',
              ' aria-label="Placement grid"',
            '>',
              this._renderCells(),
              this._renderPlacements(),
            '</div>',
          '</div>',
        '</div>',
        '<div class="bb-grid-footer">',
          '<span class="bb-grid-footer__hint">' + footerMsg + '</span>',
        '</div>',
      '</div>',
    ].join('');
    this._initGridPreview();
    if (CALIBRATE) { try { this._initCalibrateOverlay(); } catch (e) { console.error('BB calibrate:', e); } }
  };

  BundleBuilder.prototype._renderCells = function () {
    var s = this._state;
    var org = s.organizer;
    var hasPending = !!s.pendingModule;
    var html = '';
    for (var r = 0; r < org.rows; r++) {
      for (var c = 0; c < org.cols; c++) {
        html += [
          '<div class="bb-cell' + (hasPending ? ' bb-cell--selectable' : '') + '"',
            ' style="grid-column:' + (c + 1) + ';grid-row:' + (r + 1) + '"',
            ' data-x="' + c + '" data-y="' + r + '"',
            ' data-action="cell-tap"',
            ' role="' + (hasPending ? 'button' : 'gridcell') + '"',
            ' aria-label="Cell column ' + (c + 1) + ' row ' + (r + 1) + '"',
            ' tabindex="-1"',
          '></div>',
        ].join('');
      }
    }
    return html;
  };

  BundleBuilder.prototype._renderPlacements = function () {
    return this._state.placements.map(function (p) {
      return [
        '<div class="bb-placed' + (p.placementImage ? ' bb-placed--png' : '') + '"',
          ' style="grid-column:' + (p.x + 1) + '/span ' + p.w + ';grid-row:' + (p.y + 1) + '/span ' + p.h + '"',
          ' data-uid="' + p.uid + '"',
          ' draggable="true"',
          ' role="button"',
          ' tabindex="0"',
          ' aria-label="' + escHtml(p.title) + ' at column ' + (p.x + 1) + ' row ' + (p.y + 1) + '. Press Delete to remove."',
        '>',
          '<img class="bb-placed__img' + (p.placementImage ? ' bb-placed__img--placement' : '') + '"',
            ' src="' + escHtml(p.placementImage || p.image) + '"',
            ' alt="' + escHtml(p.title) + '"',
          '>',
          '<div class="bb-placed__controls">',
            '<button class="bb-placed__btn bb-placed__btn--remove"',
              ' data-action="remove-placement" data-uid="' + p.uid + '"',
              ' aria-label="Remove ' + escHtml(p.title) + '"',
              ' title="Remove (Delete)"',
            '>×</button>',
          '</div>',
        '</div>',
      ].join('');
    }).join('');
  };

  /* ── Grid preview — cell-level hover ───────────────────
     Called after every _renderGridArea. Wires delegated
     mouseover on the overlay so the preview follows the
     cell under the cursor with zero coordinate math.
     Pattern chosen: delegation (single listener on the
     overlay container), so section re-renders only need to
     call _initGridPreview again — no per-cell rebinding.
  ──────────────────────────────────────────────────────── */

  BundleBuilder.prototype._initGridPreview = function () {
    var self = this;
    var overlay = this._els.gridArea ? this._els.gridArea.querySelector('.bb-grid-overlay') : null;
    if (!overlay) return;

    this._els.gridOverlay = overlay;

    // Create the single preview overlay element and park it hidden inside the grid.
    var preview = document.createElement('div');
    preview.className = 'bb-drop-preview';
    preview.style.display = 'none';
    overlay.appendChild(preview);
    this._els.preview = preview;

    // Delegated mouseover: fires when cursor enters any cell child.
    overlay.addEventListener('mouseover', function (e) {
      var cell = e.target.closest ? e.target.closest('.bb-cell') : null;
      if (cell) self._onCellEnter(cell);
    });

    // Hide preview when cursor leaves the overlay entirely.
    overlay.addEventListener('mouseleave', function (e) {
      // Guard: ignore if still inside (e.g. crossing to a child — shouldn't happen
      // with mouseleave but be safe).
      if (!overlay.contains(e.relatedTarget)) self._hidePreview();
    });
  };

  /* Called when the cursor enters a grid cell, for both tap-to-place hover
     and drag-and-drop preview. Positions the footprint outline using the
     cell's own offsetLeft/offsetTop — no getBoundingClientRect, no math. */
  BundleBuilder.prototype._onCellEnter = function (cell) {
    var s   = this._state;
    var pm  = s.pendingModule;
    var drag = s.dragging;

    if (!pm && !drag) return;

    var preview = this._els.preview;
    if (!preview) return;

    var col = parseInt(cell.dataset.x, 10);
    var row = parseInt(cell.dataset.y, 10);

    var w, h, excludeUid;
    if (drag) {
      w          = drag.w;
      h          = drag.h;
      excludeUid = drag.source === 'grid' ? drag.uid : null;
      // Keep drop-commit coordinates in sync with last hovered cell.
      drag.ghostX = col;
      drag.ghostY = row;
    } else {
      w          = pm.rotated ? pm.module.footprintH : pm.module.footprintW;
      h          = pm.rotated ? pm.module.footprintW : pm.module.footprintH;
      excludeUid = null;
      this._previewCell = { x: col, y: row };
    }

    var org      = s.organizer;
    var cellW    = cell.offsetWidth;
    var cellH    = cell.offsetHeight;
    var inBounds = col >= 0 && row >= 0 && col + w <= org.cols && row + h <= org.rows;
    var valid    = inBounds && this._canPlace(col, row, w, h, excludeUid);

    preview.style.left    = cell.offsetLeft + 'px';
    preview.style.top     = cell.offsetTop  + 'px';
    preview.style.width   = (w * cellW) + 'px';
    preview.style.height  = (h * cellH) + 'px';
    preview.style.display = 'block';
    preview.classList.toggle('bb-drop-preview--invalid', !valid);

    var ov = this._els.gridOverlay;
    if (ov) ov.classList.toggle('bb-grid-overlay--blocked', inBounds && !valid);
  };

  BundleBuilder.prototype._hidePreview = function () {
    if (this._els.preview) this._els.preview.style.display = 'none';
    if (this._els.gridOverlay) this._els.gridOverlay.classList.remove('bb-grid-overlay--blocked');
  };

  /* Used by keyboard navigation (arrows, R) to reposition the preview
     without a live mouseover event — finds the cell DOM node by coordinate
     and delegates to _onCellEnter. */
  BundleBuilder.prototype._showPreviewAtCoord = function (col, row) {
    var ov = this._els.gridOverlay;
    if (!ov) return;
    var cell = ov.querySelector('[data-x="' + col + '"][data-y="' + row + '"]');
    if (cell) this._onCellEnter(cell);
  };

  /* ── Step 2: Tray ─────────────────────────────────────── */

  BundleBuilder.prototype._renderTray = function () {
    var s = this._state;
    var self = this;
    var cards = this.modules.map(function (m) {
      return self._moduleCardHtml(m);
    }).join('');

    var btnDisabled = s.placements.length === 0 ? ' disabled' : '';

    var dotHtml = this.modules.map(function (_, i) {
      return '<button class="bb-tray__dot' + (i === 0 ? ' is-active' : '') + '"' +
        ' aria-label="Module ' + (i + 1) + '" tabindex="-1"></button>';
    }).join('');

    this._els.tray.innerHTML = [
      '<div class="bb-tray">',
        '<h3 class="bb-tray__heading">Components</h3>',
        '<div class="bb-tray__list" role="list">' + cards + '</div>',
        '<div class="bb-tray__dots" aria-hidden="true">' + dotHtml + '</div>',
        '<div class="bb-tray__cta-wrap">',
          '<button class="bb-btn bb-btn--primary bb-tray__review"',
            ' data-action="goto-step" data-step="3"',
            btnDisabled,
            ' aria-label="Review bundle (' + s.placements.length + ' component' + (s.placements.length !== 1 ? 's' : '') + ')"',
          '>',
            'Review Bundle',
            s.placements.length > 0
              ? ' <span class="bb-tray__count">' + s.placements.length + '</span>'
              : '',
          '</button>',
        '</div>',
      '</div>',
    ].join('');

    this._initCarouselNav();
  };

  BundleBuilder.prototype._initCarouselNav = function () {
    var list = this._els.tray.querySelector('.bb-tray__list');
    var dots = this._els.tray.querySelectorAll('.bb-tray__dot');
    if (!list || !dots.length) return;

    list.addEventListener('scroll', function () {
      var cardW = list.querySelector('.bb-module-card');
      if (!cardW) return;
      var idx = Math.round(list.scrollLeft / cardW.offsetWidth);
      dots.forEach(function (d, i) { d.classList.toggle('is-active', i === idx); });
    }, { passive: true });

    dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () {
        var cardW = list.querySelector('.bb-module-card');
        if (!cardW) return;
        list.scrollTo({ left: cardW.offsetWidth * i, behavior: 'smooth' });
      });
    });

    list.addEventListener('keydown', function (e) {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      var cards = Array.from(list.querySelectorAll('.bb-module-card'));
      var focused = document.activeElement && document.activeElement.closest('.bb-module-card');
      var idx = cards.indexOf(focused);
      if (idx === -1) return;
      var next = e.key === 'ArrowLeft' ? idx - 1 : idx + 1;
      if (next < 0 || next >= cards.length) return;
      e.preventDefault();
      cards[next].focus();
      cards[next].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    });
  };

  BundleBuilder.prototype._moduleCardHtml = function (module) {
    var s = this._state;
    var org = s.organizer;

    var compatible = module.compatibleOrganizers.length === 0 ||
      module.compatibleOrganizers.indexOf(org.productId) !== -1;

    var placedCount = s.placements.filter(function (p) {
      return p.productId === module.productId;
    }).length;

    var atMax = module.maxPerBundle !== null && placedCount >= module.maxPerBundle;
    var disabled = !compatible || atMax;
    var isPending = s.pendingModule && s.pendingModule.module.productId === module.productId;

    var reason = !compatible
      ? 'Not compatible with ' + org.title
      : atMax
        ? 'Maximum ' + module.maxPerBundle + ' per bundle'
        : '';

    var selectedVid = this._moduleVariants[module.productId] || module.variants[0].id;
    var hasVariants = module.variants.length > 1;

    var variantSelect = '';
    if (hasVariants) {
      var opts = module.variants.map(function (v) {
        return '<option value="' + v.id + '"' + (v.id === selectedVid ? ' selected' : '') + '>' + escHtml(v.title) + '</option>';
      }).join('');
      variantSelect = '<select class="bb-module-card__variant-select"' +
        ' data-action="select-module-variant" data-product-id="' + module.productId + '"' +
        ' aria-label="Colour for ' + escHtml(module.title) + '">' +
        opts + '</select>';
    }

    // Hero image — heroImage → image → placementImage, never broken
    var heroSrc = module.heroImage || module.image || module.placementImage || '';
    var heroAlt = escHtml(module.title) + (module.shortDescription ? ' — ' + escHtml(module.shortDescription) : '');

    var btnLabel = atMax ? 'Max placed' : isPending ? 'Click to place' : '+ Place';
    var btnAriaLabel = isPending ? 'Cancel placement of ' + escHtml(module.title)
      : 'Place ' + escHtml(module.title);

    var descHtml = module.shortDescription
      ? '<p class="bb-module-card__desc">' + escHtml(truncate(module.shortDescription, 60)) + '</p>'
      : '';
    var capacityHtml = module.capacityHint
      ? '<span class="bb-module-card__capacity">' + escHtml(truncate(module.capacityHint, 40)) + '</span>'
      : '';

    return [
      '<div class="bb-module-card',
        disabled ? ' bb-module-card--disabled' : '',
        isPending ? ' bb-module-card--pending' : '',
      '"',
        ' role="listitem"',
        ' tabindex="0"',
        ' data-product-id="' + module.productId + '"',
        reason ? ' title="' + escHtml(reason) + '"' : '',
      '>',
        '<div class="bb-module-card__hero">',
          heroSrc ? [
            '<img class="bb-module-card__img"',
              ' src="' + heroSrc + '"',
              ' alt="' + heroAlt + '"',
              ' loading="lazy"',
            '>',
          ].join('') : '',
          '<span class="bb-module-card__footprint"',
            ' aria-label="Footprint: ' + module.footprintW + ' by ' + module.footprintH + '"',
          '>' + module.footprintW + '×' + module.footprintH + '</span>',
        '</div>',
        '<div class="bb-module-card__body">',
          '<p class="bb-module-card__title">' + escHtml(module.title) + '</p>',
          descHtml,
          capacityHtml,
          '<div class="bb-module-card__meta">',
            '<p class="bb-module-card__price">' + formatPrice(module.variants[0].price) + '</p>',
            placedCount > 0 ? '<span class="bb-module-card__count">×' + placedCount + ' placed</span>' : '',
          '</div>',
          variantSelect,
        '</div>',
        '<div class="bb-module-card__actions">',
          '<button class="bb-btn bb-btn--secondary bb-module-card__place"',
            ' data-action="tap-to-place" data-product-id="' + module.productId + '"',
            (disabled && !isPending) ? ' disabled' : '',
            ' aria-label="' + btnAriaLabel + '"',
          '>',
            btnLabel,
          '</button>',
          '<span class="bb-module-card__drag-hint" aria-hidden="true">or drag</span>',
        '</div>',
        reason ? '<p class="bb-module-card__reason">' + escHtml(reason) + '</p>' : '',
      '</div>',
    ].join('');
  };

  /* ── Step 3: Summary ──────────────────────────────────── */

  BundleBuilder.prototype._renderSummary = function () {
    var s = this._state;
    var org = s.organizer;

    // Aggregate module line items by variantId
    var lineMap = {};
    s.placements.forEach(function (p) {
      var key = p.productId + '-' + p.variantId;
      if (lineMap[key]) {
        lineMap[key].qty += 1;
      } else {
        lineMap[key] = {
          title: p.title,
          variantTitle: p.variantTitle,
          price: p.price,
          image: p.image,
          qty: 1,
        };
      }
    });

    var moduleLines = Object.keys(lineMap).map(function (k) { return lineMap[k]; });
    var modulesTotal = moduleLines.reduce(function (sum, l) { return sum + l.price * l.qty; }, 0);
    var subtotal = org.price + modulesTotal;

    var moduleRows = moduleLines.map(function (line) {
      var variantSpan = (line.variantTitle && line.variantTitle !== 'Default Title')
        ? '<span class="bb-review__line-variant">' + escHtml(line.variantTitle) + '</span>' : '';
      return [
        '<div class="bb-review__line">',
          '<img class="bb-review__img" src="' + escHtml(line.image) + '" alt="' + escHtml(line.title) + '">',
          '<div class="bb-review__line-body">',
            '<span class="bb-review__line-title">' + escHtml(line.title) + '</span>',
            variantSpan,
            '<span class="bb-review__line-qty">Qty: ' + line.qty + '</span>',
          '</div>',
          '<span class="bb-review__line-price">' + formatPrice(line.price * line.qty) + '</span>',
        '</div>',
      ].join('');
    }).join('');

    var orgVariantSpan = (org.variantTitle && org.variantTitle !== 'Default Title')
      ? '<span class="bb-review__line-variant">' + escHtml(org.variantTitle) + '</span>' : '';

    this._els.summary.innerHTML = [
      '<div class="bb-review">',
        '<h2 class="bb-section-heading">Your Bundle</h2>',
        '<div class="bb-review__lines">',
          '<div class="bb-review__line bb-review__line--organizer">',
            '<img class="bb-review__img" src="' + escHtml(org.image) + '" alt="' + escHtml(org.title) + '">',
            '<div class="bb-review__line-body">',
              '<span class="bb-review__line-title">' + escHtml(org.title) + '</span>',
              orgVariantSpan,
            '</div>',
            '<span class="bb-review__line-price">' + formatPrice(org.price) + '</span>',
          '</div>',
          moduleRows,
        '</div>',
        '<div class="bb-review__subtotal">',
          '<span>Subtotal</span>',
          '<span>' + formatPrice(subtotal) + '</span>',
        '</div>',
        '<div class="bb-review__actions">',
          '<button class="bb-btn bb-btn--ghost" data-action="goto-step" data-step="2">',
            '← Edit Layout',
          '</button>',
          '<button class="bb-btn bb-btn--primary bb-review__add-to-cart"',
            ' data-action="add-to-cart"',
            ' aria-label="Add entire bundle to cart — ' + formatPrice(subtotal) + ' total"',
          '>',
            'Add Bundle to Cart',
          '</button>',
        '</div>',
      '</div>',
    ].join('');
  };

  /* ── Grid logic ───────────────────────────────────────── */

  BundleBuilder.prototype._occupiedCells = function (excludeUid) {
    var cells = {};
    this._state.placements.forEach(function (p) {
      if (p.uid === excludeUid) return;
      for (var dx = 0; dx < p.w; dx++) {
        for (var dy = 0; dy < p.h; dy++) {
          cells[(p.x + dx) + ',' + (p.y + dy)] = true;
        }
      }
    });
    return cells;
  };

  BundleBuilder.prototype._canPlace = function (x, y, w, h, excludeUid) {
    var org = this._state.organizer;
    if (x < 0 || y < 0 || x + w > org.cols || y + h > org.rows) return false;
    var occupied = this._occupiedCells(excludeUid);
    for (var dx = 0; dx < w; dx++) {
      for (var dy = 0; dy < h; dy++) {
        if (occupied[(x + dx) + ',' + (y + dy)]) return false;
      }
    }
    return true;
  };

  BundleBuilder.prototype._firstFreeCell = function (w, h) {
    var org = this._state.organizer;
    for (var r = 0; r <= org.rows - h; r++) {
      for (var c = 0; c <= org.cols - w; c++) {
        if (this._canPlace(c, r, w, h, null)) return { x: c, y: r };
      }
    }
    return null;
  };

  BundleBuilder.prototype._addPlacement = function (module, variantId, x, y, rotated) {
    var isRotated = !!(rotated && module.rotatable);
    var variant = module.variants.filter(function (v) { return v.id === variantId; })[0]
      || module.variants[0];
    var placement = {
      uid: uid(),
      productId: module.productId,
      variantId: variant.id,
      variantTitle: variant.title,
      x: x, y: y,
      w: isRotated ? module.footprintH : module.footprintW,
      h: isRotated ? module.footprintW : module.footprintH,
      rotated: isRotated,
      rotatable: module.rotatable,
      image: module.image,
      placementImage: module.placementImage || null,
      title: module.title,
      price: variant.price,
    };
    this._state.placements = this._state.placements.concat([placement]);
    this._renderAll();
  };

  BundleBuilder.prototype._movePlacement = function (uid, x, y) {
    var p = this._state.placements.filter(function (pl) { return pl.uid === uid; })[0];
    if (!p || !this._canPlace(x, y, p.w, p.h, uid)) return;
    this._state.placements = this._state.placements.map(function (pl) {
      return pl.uid === uid ? Object.assign({}, pl, { x: x, y: y }) : pl;
    });
    this._renderAll();
  };

  BundleBuilder.prototype._removePlacement = function (uid) {
    this._state.placements = this._state.placements.filter(function (p) { return p.uid !== uid; });
    this._renderAll();
  };

  /* ── Click delegation ─────────────────────────────────── */

  BundleBuilder.prototype._onClick = function (e) {
    var s = this._state;
    var actionEl = e.target.closest('[data-action]');

    // Exit placement mode when clicking outside the grid and tray areas
    if (!actionEl && s.pendingModule) {
      var inGrid = e.target.closest('.bb-builder__grid-area');
      var inTray = e.target.closest('.bb-builder__tray');
      if (!inGrid && !inTray) { this._exitPlacementMode(); return; }
    }

    if (!actionEl) return;
    var action = actionEl.dataset.action;
    var self = this;

    switch (action) {
      case 'goto-step': {
        var n = parseInt(actionEl.dataset.step, 10);
        if (n === 1 || (n === 2 && s.organizer) || (n === 3 && s.placements.length > 0)) {
          s.step = n;
          this._renderAll();
        }
        break;
      }

      case 'select-organizer': {
        var org = this.organizers.filter(function (o) {
          return o.productId === parseInt(actionEl.dataset.productId, 10);
        })[0];
        if (!org) break;
        var v = org.variants[0];
        s.organizer = this._makeOrgState(org, v.id);
        this._renderAll();
        break;
      }

      case 'confirm-organizer': {
        if (s.organizer) { s.step = 2; this._renderAll(); }
        break;
      }

      case 'tap-to-place': {
        var module = this.modules.filter(function (m) {
          return m.productId === parseInt(actionEl.dataset.productId, 10);
        })[0];
        if (!module) break;
        if (s.pendingModule && s.pendingModule.module.productId === module.productId) {
          // Toggle off — same card clicked again
          this._exitPlacementMode();
        } else {
          var vid = this._moduleVariants[module.productId] || module.variants[0].id;
          s.pendingModule = { module: module, variantId: vid, rotated: false };
          this._previewCell = null;
          s.step = 2;
          this._renderAll();
        }
        break;
      }

      case 'cell-tap': {
        var pm = s.pendingModule;
        if (!pm) break;
        var cx = parseInt(actionEl.dataset.x, 10);
        var cy = parseInt(actionEl.dataset.y, 10);
        var fpW = pm.rotated ? pm.module.footprintH : pm.module.footprintW;
        var fpH = pm.rotated ? pm.module.footprintW : pm.module.footprintH;
        if (this._canPlace(cx, cy, fpW, fpH, null)) {
          this._addPlacement(pm.module, pm.variantId, cx, cy, pm.rotated);
          // Auto-exit if max_per_bundle reached; otherwise stay armed for chaining
          var placed = s.placements.filter(function (p) {
            return p.productId === pm.module.productId;
          }).length;
          if (pm.module.maxPerBundle !== null && placed >= pm.module.maxPerBundle) {
            this._exitPlacementMode();
          }
          // _addPlacement already called _renderAll; chaining re-renders grid with pm still set
        }
        break;
      }

      case 'cancel-pending': {
        this._exitPlacementMode();
        break;
      }

      case 'remove-placement': {
        e.stopPropagation();
        this._removePlacement(actionEl.dataset.uid);
        break;
      }

      case 'add-to-cart': {
        this._addToCart(actionEl);
        break;
      }
    }
  };

  /* ── Change delegation ────────────────────────────────── */

  BundleBuilder.prototype._onChange = function (e) {
    var t = e.target;
    var action = t.dataset.action;

    if (action === 'select-org-variant') {
      var productId = parseInt(t.dataset.productId, 10);
      var variantId = parseInt(t.value, 10);
      var org = this.organizers.filter(function (o) { return o.productId === productId; })[0];
      if (!org) return;
      var variant = org.variants.filter(function (v) { return v.id === variantId; })[0];
      if (!variant) return;
      this._state.organizer = this._makeOrgState(org, variantId);
      this._renderAll();
    }

    if (action === 'select-module-variant') {
      var pid = parseInt(t.dataset.productId, 10);
      this._moduleVariants[pid] = parseInt(t.value, 10);
    }


  };

  /* ── Keyboard navigation ──────────────────────────────── */

  BundleBuilder.prototype._onKeydown = function (e) {
    var t   = e.target;
    var s   = this._state;
    var pm  = s.pendingModule;

    /* ── Placement mode keyboard controls ────────────────── */
    if (pm) {
      if (e.key === 'Escape') {
        e.preventDefault();
        this._exitPlacementMode();
        return;
      }

      if ((e.key === 'r' || e.key === 'R') && pm.module.rotatable) {
        e.preventDefault();
        pm.rotated = !pm.rotated;
        if (this._previewCell) {
          this._showPreviewAtCoord(this._previewCell.x, this._previewCell.y);
        }
        return;
      }

      var arrows = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
      if (arrows.indexOf(e.key) !== -1) {
        e.preventDefault();
        var kW = pm.rotated ? pm.module.footprintH : pm.module.footprintW;
        var kH = pm.rotated ? pm.module.footprintW : pm.module.footprintH;
        var org = s.organizer;
        if (!this._previewCell) {
          var fc = this._firstFreeCell(kW, kH);
          this._previewCell = fc ? { x: fc.x, y: fc.y } : { x: 0, y: 0 };
        }
        var pc = this._previewCell;
        if (e.key === 'ArrowLeft')  pc.x = Math.max(0, pc.x - 1);
        if (e.key === 'ArrowRight') pc.x = Math.min(org.cols - kW, pc.x + 1);
        if (e.key === 'ArrowUp')    pc.y = Math.max(0, pc.y - 1);
        if (e.key === 'ArrowDown')  pc.y = Math.min(org.rows - kH, pc.y + 1);
        this._showPreviewAtCoord(pc.x, pc.y);
        return;
      }

      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (this._previewCell) {
          var pc2 = this._previewCell;
          var eW = pm.rotated ? pm.module.footprintH : pm.module.footprintW;
          var eH = pm.rotated ? pm.module.footprintW : pm.module.footprintH;
          if (this._canPlace(pc2.x, pc2.y, eW, eH, null)) {
            this._addPlacement(pm.module, pm.variantId, pc2.x, pc2.y, pm.rotated);
            var placed = s.placements.filter(function (p) {
              return p.productId === pm.module.productId;
            }).length;
            if (pm.module.maxPerBundle !== null && placed >= pm.module.maxPerBundle) {
              this._exitPlacementMode();
            }
          }
        }
        return;
      }

      // Allow Tab and other non-placement keys to pass through
    }

    /* ── Existing keydown handlers ───────────────────────── */
    var elUid = t.dataset && t.dataset.uid;
    var isPlaced = t.classList && t.classList.contains('bb-placed');

    if ((e.key === 'Delete' || e.key === 'Backspace') && elUid && isPlaced) {
      e.preventDefault();
      this._removePlacement(elUid);
      return;
    }

    if (elUid && isPlaced && ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].indexOf(e.key) !== -1) {
      e.preventDefault();
      var p = s.placements.filter(function (pl) { return pl.uid === elUid; })[0];
      if (!p) return;
      var dx = e.key === 'ArrowLeft' ? -1 : e.key === 'ArrowRight' ? 1 : 0;
      var dy = e.key === 'ArrowUp'   ? -1 : e.key === 'ArrowDown'  ? 1 : 0;
      this._movePlacement(elUid, p.x + dx, p.y + dy);
      var self2 = this;
      requestAnimationFrame(function () {
        var el = self2.root.querySelector('[data-uid="' + elUid + '"]');
        if (el) el.focus();
      });
    }

    if (e.key === 'Enter' && t.dataset.action === 'select-organizer') {
      t.click();
    }
  };

  /* ── Pointer / drag ──────────────────────────────────────
     Pointer Events handle both mouse drag and touch drag.
     Touch tap-to-place is handled via click delegation above.
  ──────────────────────────────────────────────────────── */

  BundleBuilder.prototype._onPointerDown = function (e) {
    if (e.button !== 0 && e.pointerType !== 'touch') return;
    // Don't initiate drag from button elements
    if (e.target.closest('button, select, input, a, [data-action]')) return;

    var moduleCard = e.target.closest('.bb-module-card:not(.bb-module-card--disabled)');
    var placedModule = e.target.closest('.bb-placed');

    if (placedModule) {
      e.preventDefault();
      this._startPointerDrag(e, 'grid', placedModule);
    } else if (moduleCard) {
      e.preventDefault();
      this._startPointerDrag(e, 'tray', moduleCard);
    }
  };

  BundleBuilder.prototype._startPointerDrag = function (e, source, el) {
    var self = this;
    var s = this._state;
    var module, variantId, w, h, dragUid;

    if (source === 'tray') {
      var pid = parseInt(el.dataset.productId, 10);
      module = this.modules.filter(function (m) { return m.productId === pid; })[0];
      variantId = this._moduleVariants[pid] || module.variants[0].id;
      w = module.footprintW;
      h = module.footprintH;
      dragUid = null;
    } else {
      dragUid = el.dataset.uid;
      var placement = s.placements.filter(function (p) { return p.uid === dragUid; })[0];
      module = this.modules.filter(function (m) { return m.productId === placement.productId; })[0];
      variantId = placement.variantId;
      w = placement.w;
      h = placement.h;
    }

    var cs = this.settings.cellSize;
    var ghost = this._els.ghost;
    var ghostSrc = module.placementImage || module.image;
    var isPng = !!module.placementImage;
    ghost.innerHTML = '<img src="' + escHtml(ghostSrc) + '" alt="" ' +
      'style="width:' + (w * cs * 0.7) + 'px;height:' + (h * cs * 0.7) + 'px;' +
      (isPng ? 'object-fit:contain;' : 'object-fit:cover;border-radius:4px;') + '">';
    ghost.style.background   = isPng ? 'transparent' : '';
    ghost.style.boxShadow    = isPng ? 'none' : '';
    ghost.style.borderRadius = isPng ? '0' : '';
    ghost.style.display = 'block';

    var ghostW = w * cs * 0.7;
    var ghostH = h * cs * 0.7;
    this._grabOffset = { dx: ghostW / 2, dy: ghostH / 2 };
    this._moveGhost(e.clientX, e.clientY);

    s.dragging = { source: source, uid: dragUid, module: module, variantId: variantId, w: w, h: h, ghostX: null, ghostY: null };

    var dragOverlay = self._els.gridArea ? self._els.gridArea.querySelector('.bb-grid-overlay') : null;
    if (dragOverlay) dragOverlay.classList.add('bb-grid-overlay--placing');

    var onMove = function (ev) { self._onPointerMove(ev); };
    var onUp   = function (ev) {
      self._onPointerUp(ev);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      var o = self._els.gridArea ? self._els.gridArea.querySelector('.bb-grid-overlay') : null;
      if (o) o.classList.remove('bb-grid-overlay--placing');
      self._hidePreview();
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);

    try { el.setPointerCapture(e.pointerId); } catch (ignore) {}
  };

  BundleBuilder.prototype._moveGhost = function (clientX, clientY) {
    var g = this._els.ghost;
    var off = this._grabOffset || { dx: 12, dy: 12 };
    g.style.left = (clientX - off.dx) + 'px';
    g.style.top  = (clientY - off.dy) + 'px';
  };

  BundleBuilder.prototype._onPointerMove = function (e) {
    var s = this._state;
    if (!s.dragging) return;
    this._moveGhost(e.clientX, e.clientY);
    // setPointerCapture redirects pointer events to the capturing element, so
    // mouseover never fires on cells under the cursor during a drag.
    // Use elementFromPoint (unaffected by capture) to resolve the cell and
    // update ghostX/ghostY via _onCellEnter — same path as the hover case.
    var el = document.elementFromPoint(e.clientX, e.clientY);
    var cell = el ? (el.closest ? el.closest('.bb-cell') : null) : null;
    if (cell) this._onCellEnter(cell);
  };

  /* ── Exit placement mode ────────────────────────────────
     Single exit point used by: Esc, toggle-off, outside-click,
     cancel button, max_per_bundle auto-exit.
  ──────────────────────────────────────────────────────── */
  BundleBuilder.prototype._exitPlacementMode = function () {
    var s = this._state;
    s.pendingModule = null;
    this._previewCell = null;
    this._hidePreview();
    this._renderAll();
  };

  BundleBuilder.prototype._onPointerUp = function (e) {
    var s = this._state;
    if (!s.dragging) return;

    this._els.ghost.style.display = 'none';

    var drag = s.dragging;
    s.dragging = null;

    if (drag.ghostX === null || drag.ghostY === null) return;

    var excludeUid = drag.source === 'grid' ? drag.uid : null;
    if (!this._canPlace(drag.ghostX, drag.ghostY, drag.w, drag.h, excludeUid)) return;

    if (drag.source === 'tray') {
      this._addPlacement(drag.module, drag.variantId, drag.ghostX, drag.ghostY);
    } else {
      this._movePlacement(drag.uid, drag.ghostX, drag.ghostY);
    }
  };

  /* ── Cart ─────────────────────────────────────────────── */

  BundleBuilder.prototype._makeOrgState = function (org, variantId) {
    var variant = org.variants.filter(function (v) { return v.id === variantId; })[0]
      || org.variants[0];
    return Object.assign({}, org, {
      variantId: variant.id,
      variantTitle: variant.title,
      price: variant.price,
      image: variant.image || org.previewImage,
      previewImage: variant.image || org.previewImage,
    });
  };

  BundleBuilder.prototype._buildCartItems = function (bundleId) {
    var s = this._state;
    var layout = JSON.stringify(s.placements.map(function (p) {
      return { uid: p.uid, productId: p.productId, variantId: p.variantId, x: p.x, y: p.y, w: p.w, h: p.h, rotated: p.rotated };
    }));

    var items = [{
      id: s.organizer.variantId,
      quantity: 1,
      properties: {
        '_bundle_id': bundleId,
        '_bundle_role': 'organizer',
        '_bundle_layout': layout,
      },
    }];

    // Aggregate modules by variantId
    var byVariant = {};
    s.placements.forEach(function (p) {
      if (byVariant[p.variantId]) {
        byVariant[p.variantId].quantity += 1;
      } else {
        byVariant[p.variantId] = {
          id: p.variantId,
          quantity: 1,
          properties: {
            '_bundle_id': bundleId,
            '_bundle_role': 'module',
          },
        };
      }
    });

    Object.keys(byVariant).forEach(function (k) { items.push(byVariant[k]); });
    return items;
  };

  BundleBuilder.prototype._addToCart = function (btn) {
    var self = this;
    if (btn) { btn.disabled = true; btn.textContent = 'Adding…'; }

    var bundleId = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : uid();

    var items = this._buildCartItems(bundleId);

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items }),
    })
    .then(function (resp) {
      if (!resp.ok) return resp.json().then(function (d) { throw new Error(d.description || d.message || 'HTTP ' + resp.status); });
      if (self.settings.redirectOnAdd) {
        window.location.href = self.settings.redirectUrl || '/cart';
      }
    })
    .catch(function (err) {
      self._showError('Couldn\'t add to cart: ' + err.message);
      if (btn) { btn.disabled = false; btn.textContent = 'Add Bundle to Cart'; }
    });
  };

  BundleBuilder.prototype._showError = function (msg) {
    var el = this._els.error;
    el.textContent = msg;
    el.hidden = false;
    setTimeout(function () { el.hidden = true; }, 8000);
  };

  /* ── Bounds calibration tool ────────────────────────────
     Activated by ?bb-calibrate=1 in the URL.
     Overlays drag handles on the preview image so you can
     visually align the grid overlay to the velcro area and
     copy the resulting bounds JSON.
  ──────────────────────────────────────────────────────── */

  BundleBuilder.prototype._initCalibrateOverlay = function () {
    var self = this;
    var org  = this._state.organizer;
    if (!org) return;

    var wrap = this._els.gridArea.querySelector('.bb-grid-preview-wrap');
    if (!wrap) return;

    // Seed from the organizer's current bounds
    var src = org.bounds || { x: 0.1, y: 0.1, w: 0.8, h: 0.8 };
    var b   = { x: src.x, y: src.y, w: src.w, h: src.h };

    // Remove stale overlay from previous render
    var old = wrap.querySelector('.bb-cal-overlay');
    if (old) old.parentNode.removeChild(old);

    var overlay = document.createElement('div');
    overlay.className = 'bb-cal-overlay';
    overlay.innerHTML =
      '<div class="bb-cal-box">' +
        '<div class="bb-cal-handle" data-dir="nw"></div>' +
        '<div class="bb-cal-handle" data-dir="ne"></div>' +
        '<div class="bb-cal-handle" data-dir="sw"></div>' +
        '<div class="bb-cal-handle" data-dir="se"></div>' +
        '<div class="bb-cal-handle" data-dir="n"></div>' +
        '<div class="bb-cal-handle" data-dir="s"></div>' +
        '<div class="bb-cal-handle" data-dir="e"></div>' +
        '<div class="bb-cal-handle" data-dir="w"></div>' +
      '</div>' +
      '<div class="bb-cal-panel">' +
        '<input class="bb-cal-json" type="text" spellcheck="false" aria-label="Bounds JSON">' +
        '<button class="bb-cal-copy">Copy</button>' +
      '</div>';

    wrap.appendChild(overlay);

    var box     = overlay.querySelector('.bb-cal-box');
    var jsonEl  = overlay.querySelector('.bb-cal-json');
    var copyBtn = overlay.querySelector('.bb-cal-copy');

    box.style.setProperty('--bb-cal-cols', org.cols);
    box.style.setProperty('--bb-cal-rows', org.rows);

    function round3(n) { return Math.round(n * 1000) / 1000; }

    function updateDisplay() {
      box.style.left   = (b.x * 100) + '%';
      box.style.top    = (b.y * 100) + '%';
      box.style.width  = (b.w * 100) + '%';
      box.style.height = (b.h * 100) + '%';
      jsonEl.value = JSON.stringify({
        x: round3(b.x), y: round3(b.y), w: round3(b.w), h: round3(b.h)
      });
    }

    jsonEl.addEventListener('input', function () {
      try {
        var p = JSON.parse(jsonEl.value);
        if (typeof p.x === 'number') b.x = Math.max(0, Math.min(1, p.x));
        if (typeof p.y === 'number') b.y = Math.max(0, Math.min(1, p.y));
        if (typeof p.w === 'number') b.w = Math.max(0.01, Math.min(1, p.w));
        if (typeof p.h === 'number') b.h = Math.max(0.01, Math.min(1, p.h));
        box.style.left   = (b.x * 100) + '%';
        box.style.top    = (b.y * 100) + '%';
        box.style.width  = (b.w * 100) + '%';
        box.style.height = (b.h * 100) + '%';
      } catch (ignore) {}
    });

    function startDrag(e, dir) {
      e.preventDefault();
      e.stopPropagation();
      var oRect   = overlay.getBoundingClientRect();
      var startX  = e.clientX;
      var startY  = e.clientY;
      var startB  = { x: b.x, y: b.y, w: b.w, h: b.h };
      var MIN     = 0.05;

      function onMove(ev) {
        var dx = (ev.clientX - startX) / oRect.width;
        var dy = (ev.clientY - startY) / oRect.height;

        if (dir === 'move') {
          b.x = Math.max(0, Math.min(1 - startB.w, startB.x + dx));
          b.y = Math.max(0, Math.min(1 - startB.h, startB.y + dy));
        } else {
          if (dir.indexOf('e') !== -1) {
            b.w = Math.max(MIN, Math.min(1 - startB.x, startB.w + dx));
          }
          if (dir.indexOf('s') !== -1) {
            b.h = Math.max(MIN, Math.min(1 - startB.y, startB.h + dy));
          }
          if (dir.indexOf('w') !== -1) {
            var nx = Math.max(0, Math.min(startB.x + startB.w - MIN, startB.x + dx));
            b.w = startB.x + startB.w - nx;
            b.x = nx;
          }
          if (dir.indexOf('n') !== -1) {
            var ny = Math.max(0, Math.min(startB.y + startB.h - MIN, startB.y + dy));
            b.h = startB.y + startB.h - ny;
            b.y = ny;
          }
        }
        updateDisplay();
      }

      function onUp() {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup',   onUp);
      }

      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup',   onUp);
      try { e.target.setPointerCapture(e.pointerId); } catch (ignore) {}
    }

    box.addEventListener('pointerdown', function (e) {
      if (e.target.dataset.dir) return;
      startDrag(e, 'move');
    });

    overlay.querySelectorAll('.bb-cal-handle').forEach(function (h) {
      h.addEventListener('pointerdown', function (e) { startDrag(e, h.dataset.dir); });
    });

    copyBtn.addEventListener('click', function () {
      var json = jsonEl.value;
      if (navigator.clipboard) {
        navigator.clipboard.writeText(json).then(function () {
          copyBtn.textContent = 'Copied!';
          setTimeout(function () { copyBtn.textContent = 'Copy'; }, 2000);
        });
      } else {
        jsonEl.select();
        document.execCommand('copy');
        copyBtn.textContent = 'Copied!';
        setTimeout(function () { copyBtn.textContent = 'Copy'; }, 2000);
      }
    });

    updateDisplay();
  };

  /* ── Auto-initialise ────────────────────────────────────
     Scans the page for [data-bundle-builder] elements and
     boots one BundleBuilder per element.
  ──────────────────────────────────────────────────────── */

  function initEl(el) {
    if (el._bundleBuilder) return;
    var dataEl = el.querySelector('script[type="application/json"][data-bb-data]');
    if (!dataEl) return;
    try {
      var data = JSON.parse(dataEl.textContent);
      el._bundleBuilder = new BundleBuilder(el, data);
    } catch (err) {
      console.error('[BundleBuilder] Failed to parse data island:', err);
    }
  }

  function init() {
    document.querySelectorAll('[data-bundle-builder]').forEach(initEl);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Re-init when Shopify theme editor hot-reloads the section */
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target;
    if (!root) return;
    var bbEl = root.querySelector('[data-bundle-builder]') ||
               (root.hasAttribute && root.hasAttribute('data-bundle-builder') ? root : null);
    if (bbEl) initEl(bbEl);
  });

})();
